import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import { isMobile } from 'react-device-detect';
import { FaLock, FaArrowRight, FaMobileAlt, FaRedo, FaEnvelope, FaWhatsapp } from 'react-icons/fa';
import { Turnstile } from '@marsidev/react-turnstile';

const Login = ({ setToken }) => {
    const [identificacion, setIdentificacion] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [welcomeMessage, setWelcomeMessage] = useState('');

    // OTP State
    const [otpStep, setOtpStep] = useState(false);
    const [otpValues, setOtpValues] = useState(['', '', '', '', '']);
    const [otpSending, setOtpSending] = useState(false);
    const [maskedPhone, setMaskedPhone] = useState('');
    const [maskedEmail, setMaskedEmail] = useState('');
    const [availableChannels, setAvailableChannels] = useState({ whatsapp: false, email: false });
    const [otpChannel, setOtpChannel] = useState('whatsapp');
    const [otpSent, setOtpSent] = useState(false);
    const [pendingEmpleado, setPendingEmpleado] = useState(null);
    const [otpToken, setOtpToken] = useState(null);
    const [otpCountdown, setOtpCountdown] = useState(0);

    // Turnstile State (solo en paso OTP / envío de código)
    const [cfToken, setCfToken] = useState('');
    const [captchaReady, setCaptchaReady] = useState(false);
    const [dynamicSiteKey, setDynamicSiteKey] = useState(null);
    const turnstileRef = useRef(null);

    const identificacionRef = useRef(null);
    const passwordRef = useRef(null);
    const otpInputRefs = useRef([]);

    useEffect(() => {
        const fetchConfig = async () => {
            try {
                const response = await axios.get('/api/auth/config');
                if (response.data.turnstileSiteKey) {
                    setDynamicSiteKey(response.data.turnstileSiteKey);
                }
            } catch (err) {
                console.error('Error fetching dynamic config:', err);
            }
        };

        fetchConfig();

        const currentPath = window.location.pathname;
        if (!Cookies.get('redirectPath') && currentPath !== '/login') {
            Cookies.set('redirectPath', currentPath, { expires: 1 });
        }

        const storedNombreCompleto = Cookies.get('nombre_empleado');
        const storedIdentificacion = Cookies.get('identificacion');

        if (storedNombreCompleto) {
            const firstName = storedNombreCompleto.split(' ')[0];
            setWelcomeMessage(`${firstName}`);
            setIdentificacion(storedIdentificacion || '');
            setTimeout(() => passwordRef.current?.focus(), 100);
        } else {
            setTimeout(() => identificacionRef.current?.focus(), 100);
        }
    }, []);

    useEffect(() => {
        if (otpCountdown > 0) {
            const timer = setTimeout(() => setOtpCountdown(otpCountdown - 1), 1000);
            return () => clearTimeout(timer);
        }
    }, [otpCountdown]);

    useEffect(() => {
        if (otpStep && otpSent) {
            setTimeout(() => {
                otpInputRefs.current[0]?.focus();
            }, 100);
        }
    }, [otpStep, otpSent]);

    const resetTurnstile = () => {
        turnstileRef.current?.reset();
        setCfToken('');
        setCaptchaReady(false);
    };

    const sendOtpCode = async (empleadoId = null, channelOverride = null) => {
        const targetEmpleado = empleadoId || pendingEmpleado;
        const channel = channelOverride || otpChannel;

        if (!targetEmpleado) {
            console.error('No hay empleado para enviar OTP');
            return;
        }

        if (SITE_KEY && !cfToken) {
            setError('Completa la verificación de seguridad antes de enviar el código.');
            return;
        }

        setOtpSending(true);
        setError('');

        try {
            const payload = {
                id_empleado: targetEmpleado,
                channel
            };
            if (cfToken) payload.cfTurnstileResponse = cfToken;

            await axios.post('/api/auth/send-otp', payload);
            setOtpChannel(channel);
            setOtpSent(true);
            setOtpCountdown(60);
            setOtpValues(['', '', '', '', '']);
            resetTurnstile();
        } catch (err) {
            const data = err.response?.data;
            const errorMsg = data?.error || 'Error al enviar código OTP';
            setError(errorMsg);

            if (data?.code === 'TURNSTILE_FAILED' || data?.code === 'TURNSTILE_REQUIRED') {
                resetTurnstile();
            }

            if (data?.code === 'OTP_DELIVERY_FAILED' && data?.channel === 'whatsapp' && availableChannels.email) {
                setError(`${errorMsg} Puedes intentar por correo electrónico.`);
            }
        } finally {
            setOtpSending(false);
        }
    };

    const [loginSuccess, setLoginSuccess] = useState(false);

    const handleLogin = async (e) => {
        e.preventDefault();

        if (!identificacion || !password) {
            setError('Por favor completa todos los campos');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const payload = { identificacion, password };
            if (otpToken) payload.otpToken = otpToken;

            const response = await axios.post('/api/auth/login', payload);
            const data = response.data;

            if (data.requiresOtp) {
                const channels = data.channels || { whatsapp: !!data.telefono_masked, email: !!data.email_masked };
                setPendingEmpleado(data.id_empleado);
                setMaskedPhone(data.telefono_masked || '');
                setMaskedEmail(data.email_masked || '');
                setAvailableChannels(channels);
                setOtpChannel(data.default_channel || (channels.whatsapp ? 'whatsapp' : 'email'));
                setOtpSent(false);
                setOtpValues(['', '', '', '', '']);
                resetTurnstile();
                setOtpStep(true);
                setLoading(false);
                return;
            }

            const { id_empleado, nombre_empleado } = data;

            Cookies.set('id_empleado', id_empleado, { expires: 1 });
            Cookies.set('nombre_empleado', nombre_empleado, { expires: 1 });
            Cookies.set('identificacion', identificacion, { expires: 1 });

            setToken('valid-session');

            const redirectPath = Cookies.get('redirectPath') || (isMobile ? '/m/productos/buscar/' : '/dashboard/welcome');
            Cookies.remove('redirectPath');

            setLoginSuccess(true);

            setTimeout(() => {
                if (id_empleado) {
                    window.location.href = redirectPath;
                } else {
                    setError('Error al iniciar sesión. Intente nuevamente.');
                    setLoginSuccess(false);
                }
            }, 800);

        } catch (err) {
            setLoading(false);
            const errorMsg = err.response?.data?.error || 'Error durante el login. Intente de nuevo.';
            setError(errorMsg);
        }
    };

    const SITE_KEY = dynamicSiteKey || (process.env.REACT_APP_CF_SITE_KEY && process.env.REACT_APP_CF_SITE_KEY !== 'undefined'
        ? process.env.REACT_APP_CF_SITE_KEY
        : null);

    const handleOtpChange = (index, value) => {
        if (value && !/^\d+$/.test(value)) return;

        const newOtpValues = [...otpValues];
        newOtpValues[index] = value;
        setOtpValues(newOtpValues);

        if (value && index < 4) {
            otpInputRefs.current[index + 1].focus();
        }
    };

    const handleOtpKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !otpValues[index] && index > 0) {
            otpInputRefs.current[index - 1].focus();
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text').slice(0, 5).replace(/\D/g, '');

        if (pastedData) {
            const newOtpValues = [...otpValues];
            for (let i = 0; i < pastedData.length; i++) {
                if (i < 5) newOtpValues[i] = pastedData[i];
            }
            setOtpValues(newOtpValues);

            const nextFocus = Math.min(pastedData.length, 4);
            otpInputRefs.current[nextFocus].focus();
        }
    };

    const handleVerifyOtp = async (e) => {
        e.preventDefault();

        const fullOtpCode = otpValues.join('');
        if (fullOtpCode.length !== 5) {
            setError('El código debe tener 5 dígitos');
            return;
        }

        setLoading(true);
        setError('');

        try {
            const verifyResponse = await axios.post('/api/auth/verify-otp', {
                id_empleado: pendingEmpleado,
                otp: fullOtpCode
            });

            if (verifyResponse.data.success) {
                setOtpToken(verifyResponse.data.otpToken);

                const loginResponse = await axios.post('/api/auth/login', {
                    identificacion,
                    password,
                    otpToken: verifyResponse.data.otpToken
                });

                const { id_empleado, nombre_empleado } = loginResponse.data;

                Cookies.set('id_empleado', id_empleado, { expires: 1 });
                Cookies.set('nombre_empleado', nombre_empleado, { expires: 1 });
                Cookies.set('identificacion', identificacion, { expires: 1 });

                setToken('valid-session');

                const redirectPath = Cookies.get('redirectPath') || (isMobile ? '/m/productos/buscar/' : '/dashboard/welcome');
                Cookies.remove('redirectPath');

                setLoginSuccess(true);

                setTimeout(() => {
                    window.location.href = redirectPath;
                }, 800);
            }
        } catch (err) {
            const errorMsg = err.response?.data?.error || 'Código inválido o expirado';
            setError(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    const handleBackToLogin = () => {
        setOtpStep(false);
        setOtpValues(['', '', '', '', '']);
        setOtpToken(null);
        setPendingEmpleado(null);
        setOtpSent(false);
        setMaskedPhone('');
        setMaskedEmail('');
        setAvailableChannels({ whatsapp: false, email: false });
        setOtpChannel('whatsapp');
        resetTurnstile();
        setError('');
    };

    const switchOtpChannel = (channel) => {
        if (channel === otpChannel) return;
        setOtpChannel(channel);
        setOtpSent(false);
        setOtpValues(['', '', '', '', '']);
        setOtpCountdown(0);
        resetTurnstile();
        setError('');
    };

    const maskedDestination = otpChannel === 'email' ? maskedEmail : maskedPhone;
    const showChannelPicker = availableChannels.whatsapp && availableChannels.email;

    const turnstileWidget = SITE_KEY ? (
        <div className="mb-4">
            <div className="relative min-h-[65px] flex justify-center bg-gray-800/30 rounded-lg p-2 border border-dashed border-gray-700/50">
                {!captchaReady && (
                    <div className="absolute inset-0 flex items-center justify-center gap-2 text-xs text-gray-500 z-0">
                        <div className="w-4 h-4 rounded-full border-2 border-gray-600 border-t-transparent animate-spin"></div>
                        <span>Preparando seguridad...</span>
                    </div>
                )}
                <div className="relative z-10">
                    <Turnstile
                        ref={turnstileRef}
                        siteKey={SITE_KEY}
                        onSuccess={(token) => {
                            setCfToken(token);
                            setCaptchaReady(true);
                            setError('');
                        }}
                        onError={() => {
                            setError('Error de conexión seguridad. Reintenta.');
                            setCaptchaReady(true);
                        }}
                        onExpire={() => {
                            setCfToken('');
                            setCaptchaReady(false);
                        }}
                        onLoad={() => setCaptchaReady(true)}
                        options={{ theme: 'dark', size: 'normal' }}
                    />
                </div>
            </div>
        </div>
    ) : null;

    return (
        <div className="min-h-screen flex items-center justify-center relative overflow-hidden animate-aurora">
            <div className="absolute inset-0 bg-black/30 backdrop-blur-[2px] z-0"></div>

            {loginSuccess && (
                <div className="absolute inset-0 flex items-center justify-center z-50 pointer-events-none overflow-hidden">
                    <div className="absolute w-10 h-10 bg-yellow-500 rounded-full animate-circle-expand"></div>
                    <div className="relative z-10 animate-pop-in flex justify-center items-center">
                        <img
                            src="/images/logo-osorio-negro.png"
                            alt="Success Logo"
                            className="w-32 md:w-48 h-auto drop-shadow-2xl object-contain"
                        />
                    </div>
                </div>
            )}

            <div className={`relative z-10 bg-gray-900/80 backdrop-blur-xl border border-gray-800 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-fade-in-up transition-opacity duration-500 ${loginSuccess ? 'opacity-0' : 'opacity-100'}`}>
                <div className="bg-gradient-to-r from-official-from via-official-via to-official-to p-1"></div>

                <div className="p-6 md:p-8">
                    <div className="flex items-center justify-between mb-8">
                        <div>
                            <h1 className="text-2xl font-bold text-white tracking-tight">
                                {otpStep ? 'Verificación' : 'Iniciar Sesión'}
                            </h1>
                            <p className="text-gray-400 text-sm mt-1">
                                {otpStep ? 'Código de seguridad' : 'Acceso administrativo'}
                            </p>
                        </div>
                        <div className="bg-gray-800/50 p-3 rounded-full text-yellow-500 border border-gray-700">
                            {otpStep ? (
                                otpChannel === 'email' ? <FaEnvelope size={20} /> : <FaMobileAlt size={20} />
                            ) : (
                                <FaLock size={20} />
                            )}
                        </div>
                    </div>

                    {!otpStep && (
                        <div className="flex justify-center mb-8">
                            <div className="w-24 h-24 bg-yellow-500 rounded-full flex items-center justify-center p-4 border border-yellow-400 shadow-lg shadow-yellow-500/20">
                                <img
                                    src="/images/lotus-h-logo-bw.png"
                                    alt="Logo"
                                    className="w-full h-auto opacity-95"
                                />
                            </div>
                        </div>
                    )}

                    {otpStep ? (
                        <div className="animate-in slide-in-from-right duration-300">
                            <div className="text-center mb-6">
                                <p className="text-gray-300 mb-2">
                                    {otpSent
                                        ? 'Ingresa el código enviado a'
                                        : 'Elige cómo recibir tu código de verificación'}
                                </p>

                                {showChannelPicker && !otpSent && (
                                    <div className="flex gap-2 justify-center mb-4">
                                        <button
                                            type="button"
                                            onClick={() => switchOtpChannel('whatsapp')}
                                            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                                                otpChannel === 'whatsapp'
                                                    ? 'bg-green-600/20 border-green-500 text-green-400'
                                                    : 'bg-gray-800 border-gray-700 text-gray-400 hover:text-white'
                                            }`}
                                        >
                                            <FaWhatsapp /> WhatsApp
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => switchOtpChannel('email')}
                                            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                                                otpChannel === 'email'
                                                    ? 'bg-blue-600/20 border-blue-500 text-blue-400'
                                                    : 'bg-gray-800 border-gray-700 text-gray-400 hover:text-white'
                                            }`}
                                        >
                                            <FaEnvelope /> Correo
                                        </button>
                                    </div>
                                )}

                                {otpSent && maskedDestination && (
                                    <div className="inline-block bg-gray-800 px-3 py-1 rounded-full border border-gray-700">
                                        <p className="text-yellow-400 font-mono text-sm tracking-wider">
                                            {otpChannel === 'email' ? '✉️' : '📱'} {maskedDestination}
                                        </p>
                                    </div>
                                )}

                                {!otpSent && !showChannelPicker && maskedDestination && (
                                    <div className="inline-block bg-gray-800 px-3 py-1 rounded-full border border-gray-700">
                                        <p className="text-yellow-400 font-mono text-sm tracking-wider">
                                            {otpChannel === 'email' ? '✉️' : '📱'}{' '}
                                            {otpChannel === 'email' ? maskedEmail : maskedPhone}
                                        </p>
                                    </div>
                                )}
                            </div>

                            {!otpSent ? (
                                <div>
                                    {turnstileWidget}

                                    {error && (
                                        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 mb-4">
                                            <p className="text-red-400 text-sm text-center font-medium">{error}</p>
                                        </div>
                                    )}

                                    <button
                                        type="button"
                                        onClick={() => sendOtpCode()}
                                        disabled={otpSending || (SITE_KEY && !cfToken)}
                                        className="group w-full bg-gradient-to-r from-official-from via-official-via to-official-to hover:brightness-110 text-white font-bold py-3 px-4 rounded-xl flex items-center justify-center transition-all disabled:opacity-50 disabled:cursor-not-allowed mb-4"
                                    >
                                        {otpSending ? (
                                            <span className="flex items-center gap-2">
                                                <FaRedo className="animate-spin" /> Enviando...
                                            </span>
                                        ) : (
                                            <span>Enviar código</span>
                                        )}
                                    </button>

                                    <button
                                        type="button"
                                        onClick={handleBackToLogin}
                                        className="w-full text-gray-500 hover:text-white text-sm transition-colors"
                                    >
                                        ← Volver al login
                                    </button>
                                </div>
                            ) : (
                                <form onSubmit={handleVerifyOtp} autoComplete="on">
                                    <input
                                        type="text"
                                        autoComplete="one-time-code"
                                        className="sr-only"
                                        onChange={(e) => {
                                            const val = e.target.value.slice(0, 5).replace(/\D/g, '');
                                            if (val) {
                                                const newVals = [...otpValues];
                                                for (let i = 0; i < val.length; i++) newVals[i] = val[i];
                                                setOtpValues(newVals);
                                            }
                                        }}
                                    />

                                    <div className="flex justify-center gap-2 mb-8" onPaste={handlePaste}>
                                        {otpValues.map((digit, index) => (
                                            <input
                                                key={index}
                                                ref={el => otpInputRefs.current[index] = el}
                                                type="text"
                                                inputMode="numeric"
                                                pattern="[0-9]*"
                                                maxLength={1}
                                                value={digit}
                                                onChange={(e) => handleOtpChange(index, e.target.value)}
                                                onKeyDown={(e) => handleOtpKeyDown(index, e)}
                                                className="w-12 h-14 md:w-14 md:h-16 text-center text-xl md:text-2xl font-bold bg-gray-800 border-2 border-gray-700 rounded-lg text-white focus:border-yellow-500 focus:ring-0 focus:outline-none transition-all shadow-inner"
                                                required
                                            />
                                        ))}
                                    </div>

                                    {error && (
                                        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 mb-6">
                                            <p className="text-red-400 text-sm text-center font-medium">{error}</p>
                                        </div>
                                    )}

                                    <button
                                        type="submit"
                                        disabled={loading || otpValues.some(v => v === '')}
                                        className="group w-full bg-gradient-to-r from-gray-100 to-gray-300 hover:from-white hover:to-gray-100 text-gray-900 font-bold py-3 px-4 rounded-xl flex items-center justify-center transition-all disabled:opacity-50 disabled:cursor-not-allowed transform active:scale-[0.98]"
                                    >
                                        {loading ? (
                                            <span className="flex items-center gap-2">
                                                <FaRedo className="animate-spin" /> Verificando...
                                            </span>
                                        ) : (
                                            <>
                                                <span className="mr-2">Verificar Acceso</span>
                                                <FaArrowRight className="group-hover:translate-x-1 transition-transform" size={14} />
                                            </>
                                        )}
                                    </button>

                                    <div className="mt-4 flex flex-col gap-2 text-sm">
                                        {showChannelPicker && (
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    const alt = otpChannel === 'whatsapp' ? 'email' : 'whatsapp';
                                                    switchOtpChannel(alt);
                                                }}
                                                className="text-center text-yellow-500/90 hover:text-yellow-400 transition-colors"
                                            >
                                                {otpChannel === 'whatsapp'
                                                    ? '¿No llegó por WhatsApp? Enviar por correo'
                                                    : '¿No llegó al correo? Enviar por WhatsApp'}
                                            </button>
                                        )}

                                        <div className="flex justify-between items-center">
                                            <button
                                                type="button"
                                                onClick={handleBackToLogin}
                                                className="text-gray-500 hover:text-white transition-colors"
                                            >
                                                ← Volver
                                            </button>

                                            {otpCountdown > 0 ? (
                                                <span className="text-gray-500 font-mono">
                                                    Reenviar en {otpCountdown}s
                                                </span>
                                            ) : (
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setOtpSent(false);
                                                        resetTurnstile();
                                                    }}
                                                    disabled={otpSending}
                                                    className="text-yellow-500 hover:text-yellow-400 font-medium flex items-center gap-1 transition-colors"
                                                >
                                                    {otpSending ? <FaRedo className="animate-spin" /> : <FaRedo size={12} />}
                                                    {otpSending ? 'Enviando...' : 'Reenviar código'}
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </form>
                            )}
                        </div>
                    ) : (
                        <div className="animate-in fade-in duration-500">
                            {welcomeMessage && (
                                <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3 mb-6 flex items-center gap-3">
                                    <div className="bg-blue-500/20 p-2 rounded-full">
                                        <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
                                    </div>
                                    <p className="text-blue-100 text-sm">
                                        Hola de nuevo, <strong className="text-white">{welcomeMessage}</strong>
                                    </p>
                                </div>
                            )}

                            <form onSubmit={handleLogin}>
                                <div className="space-y-4 mb-6">
                                    <div className="group">
                                        <label className="block text-xs font-medium text-gray-500 mb-1 ml-1 group-focus-within:text-yellow-500 transition-colors uppercase tracking-wider">Identificación</label>
                                        <input
                                            ref={identificacionRef}
                                            type="text"
                                            value={identificacion}
                                            onChange={(e) => setIdentificacion(e.target.value)}
                                            required
                                            className="w-full bg-gray-800/50 border border-gray-700 text-gray-100 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-yellow-500/50 focus:border-yellow-500 transition-all placeholder-gray-600"
                                            placeholder="Ingresa tu ID"
                                            autoComplete="username"
                                        />
                                    </div>

                                    <div className="group">
                                        <label className="block text-xs font-medium text-gray-500 mb-1 ml-1 group-focus-within:text-yellow-500 transition-colors uppercase tracking-wider">Contraseña</label>
                                        <input
                                            ref={passwordRef}
                                            type="password"
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                            required
                                            className="w-full bg-gray-800/50 border border-gray-700 text-gray-100 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-yellow-500/50 focus:border-yellow-500 transition-all placeholder-gray-600"
                                            placeholder="••••••••"
                                            autoComplete="current-password"
                                        />
                                    </div>
                                </div>

                                {error && (
                                    <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 mb-6 animate-pulse">
                                        <p className="text-red-400 text-sm text-center font-medium">{error}</p>
                                    </div>
                                )}

                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="group w-full bg-gradient-to-r from-official-from via-official-via to-official-to hover:brightness-110 text-white font-bold py-3 px-4 rounded-xl flex items-center justify-center transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-yellow-900/20 active:scale-[0.98]"
                                >
                                    {loading ? (
                                        <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                                    ) : (
                                        <>
                                            <span className="mr-2">Iniciar Sesión</span>
                                            <FaArrowRight className="group-hover:translate-x-1 transition-transform" size={14} />
                                        </>
                                    )}
                                </button>
                            </form>
                        </div>
                    )}
                </div>

                <div className="bg-gray-950/50 p-4 text-center border-t border-gray-800">
                    <p className="text-gray-600 text-xs">
                        &copy; {new Date().getFullYear()} Osorio Repuestos. Acceso restringido.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default Login;
